/**
 * AI helper DTOs.
 *
 * Covers the three AI surfaces mobile needs in v1:
 *
 *   - Job extraction: paste PDF/text → structured project fields (matches
 *     web `jobExtractionOutputSchema` in lib/validators.ts).
 *   - Voice transcription: short audio clip → text + suggested fields.
 *   - Section suggestion: per-wizard-section AI assist (CPP/RAMS/SI/EP/TT).
 *
 * The AIActionType enum mirrors Prisma `AIActionType`
 * (prisma/schema.prisma:64) so server-side logging stays consistent. Not
 * every action is callable from mobile in v1 — some are admin-only or
 * funnel-driven — but the enum is exhaustive for completeness.
 */
import { z } from "zod";
import { countSchema, entityIdSchema } from "../common";
import { ContractorRoleSchema } from "./project";

export const AI_ACTION_TYPES = [
  "JOB_EXTRACTION",
  "INSPECTION_SUMMARY",
  "PLAIN_ENGLISH_REWRITE",
  "HAZARD_SUGGESTION",
  "VOICE_TRANSCRIPTION",
  "CPP_SECTION_FILL",
  "TOOLBOX_TALK_FILL",
  "PCPP_EXTRACTION",
  "SI_SECTION_FILL",
  "EP_SECTION_FILL",
  "SOCIAL_POST",
  "SDS_EXTRACTION",
  "SPECIFICITY_CHECK",
  "BLOG_GENERATION",
  "DEMO_RAMS",
  "PHOTO_TO_RAMS",
  "HSE_FEED_DRAFT",
  "SOCIAL_IMAGE_GEN",
] as const;

export type AIActionType = (typeof AI_ACTION_TYPES)[number];

export const AIActionTypeSchema = z.enum(AI_ACTION_TYPES);

/**
 * Document-type discriminator for section-fill requests.
 *
 * Phase 09b: added `CPP_LIGHTWEIGHT` (CPP wizard variant, see
 * `app/api/ai/lightweight-cpp-suggest/route.ts`) and `TOOLBOX_TALK`
 * (folded into the dispatcher despite having a dedicated web endpoint —
 * mobile uses a single section-suggest entrypoint).
 */
export const AI_SECTION_DOC_TYPES = [
  "RAMS",
  "CPP",
  "CPP_LIGHTWEIGHT",
  "SITE_INDUCTION",
  "EMERGENCY_PLAN",
  "TOOLBOX_TALK",
] as const;

export type AISectionDocType = (typeof AI_SECTION_DOC_TYPES)[number];

export const AISectionDocTypeSchema = z.enum(AI_SECTION_DOC_TYPES);

// ─── Job extraction ────────────────────────────────────────────────────

export const AIJobExtractRequest = z
  .object({
    sourceText: z
      .string()
      .min(1)
      .describe(
        "Free-form text the AI should pull project fields out of. Mobile typically pastes from a tender doc or transcribed voice note.",
      ),
    sourceUrl: z
      .string()
      .optional()
      .describe(
        "Optional Spaces URL of a PDF/image to extract from. When set, server fetches and OCRs.",
      ),
  })
  .strict();

export type AIJobExtractRequest = z.infer<typeof AIJobExtractRequest>;

/**
 * Mirrors lib/validators.ts::jobExtractionOutputSchema. Every field is
 * nullable because extraction may not find a value.
 *
 * RESPONSE schema — deliberately NOT `.strict()` (issue #2411). A mobile
 * build parses this with the schema it was compiled against, so a server that
 * adds a field would make an installed build FAIL to parse rather than ignore
 * the new key. `.strict()` stays on the REQUEST schemas in this file, which
 * the server validates against input we control. Enforced by
 * `__tests__/contracts/mobile-wire-strictness.test.ts`.
 */
export const AIJobExtractResponse = z
  .object({
    title: z.string().nullable(),
    scopeDescription: z.string().nullable(),
    siteAddress: z.string().nullable(),
    estimatedDuration: z.string().nullable(),
    contractorCount: countSchema.nullable(),
    domesticClient: z.boolean().nullable(),
    startDate: z.string().nullable(),
    hazardKeywords: z.array(z.string()),
    contractorRole: ContractorRoleSchema.nullable(),
  });

export type AIJobExtractResponse = z.infer<typeof AIJobExtractResponse>;

/**
 * PDF-import variant of job extraction.
 *
 * Instead of the JSON `sourceText` path, the mobile client may POST a PDF
 * (tender / spec) as `multipart/form-data` with a single `file` field. The
 * route extracts text server-side (React Native can't run the browser pdfjs
 * worker) and feeds it into the SAME `jobExtractionOutputSchema` AI call,
 * returning the `AIJobExtractResponse` shape. These constants keep the
 * client and route in agreement on the field name + size ceiling; the route
 * reads the multipart `file` directly rather than parsing a JSON schema.
 */
export const JOB_EXTRACT_FILE_FIELD = "file";
export const JOB_EXTRACT_MAX_PDF_BYTES = 20 * 1024 * 1024; // 20 MB.

// ─── Voice transcription ───────────────────────────────────────────────

export const AITranscribeRequest = z
  .object({
    audioKey: z
      .string()
      .describe(
        "Spaces key of the uploaded audio clip (m4a/wav/webm). Use the presigned upload flow to land the file first.",
      ),
    context: z
      .enum(["job_extract", "diary_note", "incident_report", "freeform"])
      .default("freeform")
      .describe(
        "Downstream usage hint — server may apply a context-aware prompt (e.g. expand abbreviations like 'CSCS').",
      ),
  })
  .strict();

export type AITranscribeRequest = z.infer<typeof AITranscribeRequest>;

export const AITranscribeResponse = z.object({
  text: z.string(),
  // The mobile route only returns `{ text }` today — Whisper does not surface
  // duration/language from the provider call we use. These stay optional (and
  // the object is non-strict) so the client tolerates the lean response while
  // remaining forward-compatible if the server adds them later (audit #28).
  durationSeconds: z.number().nonnegative().optional(),
  language: z.string().describe("BCP-47 tag, e.g. en-GB.").optional(),
});

export type AITranscribeResponse = z.infer<typeof AITranscribeResponse>;

// ─── Section suggestion ────────────────────────────────────────────────

export const AISectionSuggestRequest = z
  .object({
    documentType: AISectionDocTypeSchema,
    sectionKey: z
      .string()
      .min(1)
      .describe(
        "Wizard-specific section identifier (e.g. 'welfareArrangements', 'emergencyProcedures'). Mobile passes the same key the web wizard uses.",
      ),
    projectId: entityIdSchema,
    /**
     * Optional draft the user has already typed — when present, server runs
     * an "improve this" prompt instead of a from-scratch suggestion.
     */
    currentDraft: z.string().optional(),
  })
  .strict();

export type AISectionSuggestRequest = z.infer<typeof AISectionSuggestRequest>;

/** RESPONSE schema — deliberately NOT `.strict()`; see `AIJobExtractResponse`
 * above and issue #2411. */
export const AISectionSuggestResponse = z
  .object({
    suggestion: z
      .string()
      .describe("Plain-text suggestion the mobile UI can paste into the section field."),
    citations: z
      .array(z.string())
      .describe(
        "Source references (project fields, hazard catalogue entries) the suggestion drew from.",
      ),
    /** A site fact the AI cannot verify; suggestion stays empty and unusable. */
    needsInput: z.string().optional(),
  });

export type AISectionSuggestResponse = z.infer<typeof AISectionSuggestResponse>;

// ─── SDS (Safety Data Sheet) extraction ────────────────────────────────

/**
 * Mobile SDS extraction request.
 *
 * The PDF bytes themselves are sent as a `multipart/form-data` body with a
 * single `file` field (mirrors the audio path the transcribe route already
 * supports). No JSON body is used, so this schema documents the wire
 * contract for completeness but is not parsed at the route boundary — the
 * route reads the multipart `file` directly. Kept here so the mobile client
 * and server agree on the field name + limits.
 */
export const SDS_EXTRACT_FILE_FIELD = "file";
export const SDS_MAX_PDF_BYTES = 20 * 1024 * 1024; // 20 MB — matches presign `uploaded_sds` policy.

/**
 * Mirrors `lib/coshh/types.ts::SdsExtraction`. Every string field defaults
 * to "" and arrays to [] when the model can't find a value, so the mobile
 * review form always has something to bind to.
 *
 * RESPONSE schema — deliberately NOT `.strict()`; see `AIJobExtractResponse`
 * above and issue #2411. `uploadedSdsId` is the precedent this note exists
 * for: it was added as `.optional()` "for backward-compat with older
 * clients", but under `.strict()` an older client did not ignore the new key,
 * it failed to parse.
 */
export const SdsExtractionSchema = z.object({
    productName: z.string(),
    manufacturer: z.string(),
    hazardStatements: z.array(z.string()),
    precautionaryStatements: z.array(z.string()),
    ppe: z.string(),
    firstAid: z.string(),
    handlingStorage: z.string(),
    fireMeasures: z.string(),
    disposal: z.string(),
    healthEffects: z.string(),
    exposureRoutes: z.string(),
    welLimit: z.string().nullable(),
  });

export const AISdsExtractResponse = SdsExtractionSchema
  .extend({
    /** Per-field 0-1 extraction confidence. Keys are a subset of the fields above. */
    confidence: z.record(z.string(), z.number()),
    /**
     * Id of the persisted UploadedSDS row whose source PDF was stored privately.
     * Optional for backward-compat with older clients; lets the mobile save step
     * relink the stored SDS onto the substance it creates (web parity, PR #1014).
     */
    uploadedSdsId: z.string().optional(),
  });

export type AISdsExtractResponse = z.infer<typeof AISdsExtractResponse>;

/** A successfully extracted, account-owned SDS available to reuse. */
export const UploadedSdsLibraryItem = z.object({
  id: z.string(),
  originalFilename: z.string(),
  createdAt: z.string().datetime(),
  extraction: SdsExtractionSchema,
  confidence: z.record(z.string(), z.number()),
  readUrl: z.string().url().nullable(),
  linked: z.boolean(),
});
export type UploadedSdsLibraryItem = z.infer<typeof UploadedSdsLibraryItem>;

export const UploadedSdsLibraryPage = z.object({
  uploads: z.array(UploadedSdsLibraryItem),
  nextCursor: z.string().nullable(),
  canManage: z.boolean().optional(),
});
export type UploadedSdsLibraryPage = z.infer<typeof UploadedSdsLibraryPage>;
